import React from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';
import { Button } from '@mui/material';

import { useDispatch, useSelector } from '../../store/utils';
import { variablesChange } from '../../store/actions';
import { ButtonBox } from '../../molecules/button-box';

import { InputBox } from '../../molecules/input-box';
import { ParentPaper } from '../../atoms/parent-paper';
import { SliderBox } from '../../molecules/slider-box';
import { SwitchBox } from '../../molecules/switch-box';
import { ValueBox } from '../../molecules/value-box';
import { Group } from '../../organisms/group';
import { FormattedValue } from '../../store/formatted-value';
import { Board, Inverter } from '../../shared';
import { InverterTypeSelect } from '../../organisms/inverter-type-select';

import { NetworkSettings } from '../../organisms/network/network-settings';
import { WifiApPassphraseEdit } from '../../organisms/network/wifi-ap-passphrase-edit';
import { ControlBox } from '../../atoms/control-box';
import { ThermostatSettingsService } from '../../organisms/settings/thermostat-settings-service';
import { SpotSettingsService } from '../../organisms/settings/spot-settings-service';
import { TownCodeSelect } from '../../atoms/town-code-select';
import { VersionBox } from '../../organisms/settings/version-box';
import { RelaySettingsService } from './relay-settings-service';
import { HelpButton } from '../../atoms/help-button';
import { StatusDot } from '../../atoms/status-dot';
import { WallboxTypeSelect } from '../../organisms/wallbox-type-select';
import { HeatPumpTypeSelect } from '../../organisms/heat-pump-type-select';
import { InitialWizardBanner } from '../initial-wizard/initial-wizard-banner';
import { BoilerTestTrigger } from '../../organisms/boiler-test-wizard/boiler-test-trigger';

import { SettingsInstallation } from './boxes/settings-installation';
import { SettingsCircuitBreaker } from './boxes/settings-circuit-breaker';
import { SettingsPvStringNumber } from './boxes/settings-pv-string-number';
import { SettingsPvBattery } from './boxes/settings-pv-battery';
import { SettingsPvOverflow } from './boxes/settings-pv-overflow';
import { SettingsPvNominal } from './boxes/settings-pv-nominal';
import { SettingsHw } from './boxes/settings-hw';
import { SettingsWbInstall } from './boxes/settings-wb-install';
import { AppUpdate } from './app-update';

export const SettingsService = () => {
  const serviceActive = useSelector(
    ({ variables }) => variables.SERVICE_ACTIVE
  );
  const resetErr = () => {
    dispatch(variablesChange({ ERR_RESET: true }));
  };
  const dispatch = useDispatch();
  const exitService = () => {
    dispatch(variablesChange({ SERVICE_ACTIVE: false, SERVICE_PASSWORD: '' }));
  };

  const sofarGoodweNext = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.SofarSolarRS485 ||
      inverterConfigArray[0]?.type === Inverter.GoodWeNext
    );
  });
  const ems = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.SolaxEMS1000 ||
      inverterConfigArray[0]?.type === Inverter.SolaxEMS1000PRO
    );
  });

  const solax = useSelector((s) => {
    const pvInverterType = s.variables.PV_INVERTER_TYPE;
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.SolaxNext ||
      inverterConfigArray[0]?.type === Inverter.SolaxEMS1000 ||
      inverterConfigArray[0]?.type === Inverter.SolaxEMS1000PRO ||
      pvInverterType === Inverter.Solax
    );
  });

  const fronius = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.Fronius ||
      inverterConfigArray[0]?.type === Inverter.FroniusVerto
    );
  });

  const readonlyInverters = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.Hes ||
      inverterConfigArray[0]?.type === Inverter.ThreeMarPLC ||
      inverterConfigArray[0]?.type === Inverter.Sermatec
    );
  });

  const solintegWattsonic = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.Solinteg ||
      inverterConfigArray[0]?.type === Inverter.Wattsonic
    );
  });
  const relaySettings = useSelector(
    (s) =>
      (get(s.variables, 'BOARD') === Board.V2 ||
        get(s.variables, 'BOARD') === Board.SunBerry) &&
      (get(s.variables, 'LICENSE') == 3 || get(s.variables, 'LICENSE') == 5)
  );

  const networkCaps = useSelector((s) => s.variables.NETWORK_CAPABILITIES);

  const hpNominalFromHp = useSelector(
    (s) => get(s.variables, 'HP_TYPE') == 2 || get(s.variables, 'HP_TYPE') == 4
  );

  const unbalancedOutputEnabled = useSelector(
    (s) => s.variables.PV_UNBALANCED_OUTPUT_ENABLED
  );
  const backupEnabled = useSelector((s) => s.variables.PV_BACKUP_ENABLED);

  const servicePasswordInput = (
    <InputBox
      title={
        <FormattedMessage
          id="settings.service.password"
          defaultMessage="Přístup do servisu"
        />
      }
      inputVariableId="SERVICE_PASSWORD"
      label={
        <FormattedMessage
          id="settings.service.password.label"
          defaultMessage="Heslo"
        />
      }
      fullWidth
    />
  );

  const content = (
    <>
      <Group
        title={
          <FormattedMessage
            id="settings.docs"
            defaultMessage="Návody a další podklady"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <ControlBox
            title={
              <FormattedMessage
                id="settings.docs.tech_wiki.title"
                defaultMessage="Technická wiki"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.docs.tech_wiki.subtitle"
                defaultMessage="Návody, postupy a další informace"
              />
            }
            fullWidth
            control={
              <Button
                variant="contained"
                href="https://infigy.cz/app-redirect/tech-wiki"
                target="_blank"
              >
                <FormattedMessage
                  id="settings.docs.tech_wiki.button"
                  defaultMessage="Přejít na wiki"
                />
              </Button>
            }
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.utility"
            defaultMessage="Parametry domu"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <SettingsCircuitBreaker />
          <SliderBox
            title={
              <FormattedMessage
                id="settings.utility.settings.loadpercent"
                defaultMessage="Krizové zatížení"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.utility.settings.loadpercent_max"
                defaultMessage="Při překročení dochází k omezování spotřeby"
              />
            }
            variableId="EM_LOAD_MAX"
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="settings.utility.loadpercent"
                defaultMessage="Aktuální zatížení jističe"
              />
            }
            value={
              <FormattedValue
                variableId="EM_ACTUAL_LOAD_MAX"
                opts={{
                  style: 'percent',
                  maximumFractionDigits: 0,
                }}
              />
            }
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.photovoltaics"
            defaultMessage="Fotovoltaická elektrárna"
          />
        }
        visibility={{ variableId: 'PV_INSTALL_V1' }}
        layout="list"
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.photovoltaics.enabled"
                defaultMessage="FVE nainstalována"
              />
            }
            switchVariableId="PV_INSTALL"
            fullWidth
          />
          <InverterTypeSelect />
          {!fronius && !readonlyInverters && !ems && (
            <ControlBox
              title={
                <FormattedMessage
                  id="settings.photovoltaics.unbalanced_enabled"
                  defaultMessage="Asymetrie střídače"
                />
              }
              control={<StatusDot alive={unbalancedOutputEnabled} />}
              fullWidth
              subtitle={
                <FormattedMessage
                  id="settings.photovoltaics.unbalanced_enabled.subtitle"
                  defaultMessage="Informace o nastavení režimu asymetrie střídače."
                />
              }
            />
          )}
          {!fronius && !readonlyInverters && !solax && !ems && (
            <ControlBox
              title={
                <FormattedMessage
                  id="settings.photovoltaics.backup_enabled"
                  defaultMessage="Backup povolen"
                />
              }
              control={<StatusDot alive={backupEnabled} />}
              fullWidth
              subtitle={
                <FormattedMessage
                  id="settings.photovoltaics.backup_enabled.subtitle"
                  defaultMessage="Informace o nastavení režimu backup střídače."
                />
              }
            />
          )}
          {solintegWattsonic && (
            <SliderBox
              title={
                <>
                  <FormattedMessage
                    id="settings.PV.NominalInverterPower"
                    defaultMessage="Nominální výkon střídače"
                  />
                  <HelpButton
                    variant="dialog"
                    title={
                      <FormattedMessage
                        id="settings.PV.NominalInverterPower"
                        defaultMessage="Nominální výkon střídače"
                      />
                    }
                    content={
                      <FormattedMessage
                        id="settings.PV.NominalInverterPower.help"
                        defaultMessage="Pro Solinteg, Wattsonic a Deye je toto důležité pro správný výpočet přetoků do sítě."
                      />
                    }
                  />
                </>
              }
              subtitle={
                <FormattedMessage
                  id="settings.PV.NominalInverterPower.subtitle"
                  defaultMessage="Nominální výkon střídače v kW, důležitý pro výpočet % přetoků do sítě."
                />
              }
              variableId="INVERTER_POWER"
              opts={{
                maximumFractionDigits: 0,
                style: 'unit',
                unit: 'kilowatt',
              }}
              step={1}
              min={1}
              max={50}
            />
          )}
          <SettingsPvStringNumber />
          <SettingsPvNominal />
        </ParentPaper>
        <ParentPaper>
          <SettingsPvOverflow />
        </ParentPaper>
        <ParentPaper>
          <SettingsPvBattery />
        </ParentPaper>
        <ParentPaper>
          {sofarGoodweNext && (
            <SliderBox
              title={
                <FormattedMessage
                  id="settings.photovoltaics.batterychargingpower"
                  defaultMessage="Nabíjecí výkon baterie"
                />
              }
              subtitle={
                <FormattedMessage
                  id="settings.photovoltaics.batterychargingpower_sub"
                  defaultMessage="Výkon pro manuální nabíjení baterie a spotové nabíjení z distribuční sítě"
                />
              }
              variableId="PV_BATTERY_CHARGING_POWER"
              visibility={{
                variableIds: [
                  { variableId: 'PV_ONGRID', reversed: true },
                  'PV_INSTALL',
                  'PV_BATTERY',
                ],
              }}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 0,
              }}
              step={1}
              min={1}
              max={10}
            />
          )}
          {sofarGoodweNext && (
            <SliderBox
              title={
                <FormattedMessage
                  id="settings.photovoltaics.batterydischargingpower"
                  defaultMessage="Vybíjecí výkon baterie"
                />
              }
              subtitle={
                <FormattedMessage
                  id="settings.photovoltaics.batterydischargingpower_sub"
                  defaultMessage="Výkon pro spotové vybíjení baterie do distribuční sítě"
                />
              }
              variableId="PV_BATTERY_DISCHARGING_POWER"
              visibility={{
                variableIds: [
                  { variableId: 'PV_ONGRID', reversed: true },
                  'PV_INSTALL',
                  'PV_BATTERY',
                ],
              }}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 0,
              }}
              step={1}
              min={1}
              max={10}
            />
          )}
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="settings.photovoltaics.inverters-nominal"
                defaultMessage="Nominální výkon řiditelných střídačů"
              />
            }
            visibility={{ variableId: 'PV_ACTIVE_POWER_CONTROL' }}
            value={
              <FormattedValue
                variableId="PV_ACTIVE_POWER_NOMINAL"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }}
              />
            }
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="settings.photovoltaics.inverters-adjust-power"
                defaultMessage="Aktuální výkon střídačů"
              />
            }
            visibility={{ variableId: 'PV_ACTIVE_POWER_CONTROL' }}
            value={
              <FormattedValue
                variableId="PV_ACTIVE_POWER_PERC"
                opts={{
                  style: 'percent',
                  maximumFractionDigits: 0,
                }}
              />
            }
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage id="settings.boiler" defaultMessage="Bojler" />
        }
        layout="list"
        visibility={{ variableId: 'HW_INSTALL_V1' }}
      >
        <ParentPaper>
          <SettingsHw showShelliesControl />
          <BoilerTestTrigger />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.heatpump"
            defaultMessage="Tepelné čerpadlo"
          />
        }
        layout="list"
        visibility={{ variableId: 'HP_ENABLED_V1' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.enabled"
                defaultMessage="Tepelné čerpadlo nainstalováno"
              />
            }
            switchVariableId="HP_ENABLED"
            fullWidth
          />
          <HeatPumpTypeSelect />
          {!hpNominalFromHp && (
            <SliderBox
              title={
                <FormattedMessage
                  id="settings.heatpump.nominal"
                  defaultMessage="Nominální příkon"
                />
              }
              variableId="HP_NOMINAL"
              visibility={{ variableId: 'HP_ENABLED' }}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
              step={0.1}
            />
          )}
          {hpNominalFromHp && (
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="settings.heatpump.nominal1"
                  defaultMessage="Nominální příkon"
                />
              }
              value={
                <FormattedValue
                  variableId="HP_NOMINAL"
                  opts={{
                    style: 'unit',
                    unit: 'kilowatt',
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }}
                />
              }
              visibility={{ variableId: 'HP_ENABLED' }}
            />
          )}

          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.flexiRoom"
                defaultMessage="Flexibilita místnosti"
              />
            }
            switchVariableId="HP_FLEXI_ROOM"
            visibility={{ variableId: 'HP_ENABLED' }}
            fullWidth
          />

          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.flexiHw"
                defaultMessage="Flexibilita teplé vody"
              />
            }
            switchVariableId="HP_FLEXI_HW"
            visibility={{ variableId: 'HP_ENABLED' }}
            fullWidth
          />

          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.flexiCool"
                defaultMessage="Flexibilita chlazení"
              />
            }
            switchVariableId="HP_FLEXI_COOL"
            visibility={{ variableId: 'HP_ENABLED' }}
            fullWidth
          />

          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.flexiAN"
                defaultMessage="Flexibilita AN"
              />
            }
            switchVariableId="HP_FLEXI_AN"
            visibility={{
              variableIds: ['HP_ECOONE', 'HP_ENABLED'],
            }}
            fullWidth
          />
        </ParentPaper>
      </Group>
      {relaySettings && (
        <Group
          title={
            <FormattedMessage
              id="settings.service.thermostat"
              defaultMessage="Termostat"
            />
          }
          layout="list"
          visibility={{
            variableIds: ['THERMOSTAT_INSTALL_V1'],
          }}
        >
          <ThermostatSettingsService />
        </Group>
      )}
      <Group
        title={
          <FormattedMessage
            id="settings.service.spot"
            defaultMessage="Spotové obchodování"
          />
        }
        layout="list"
        visibility={{ variableId: 'SP_INSTALL_V1' }}
      >
        <SpotSettingsService />
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.wallbox.spot"
            defaultMessage="Wallbox"
          />
        }
        layout="list"
        visibility={{ variableId: 'WB_INSTALL_V1' }}
      >
        <ParentPaper>
          <SettingsWbInstall />

          <WallboxTypeSelect />
        </ParentPaper>
      </Group>

      <Group
        title={<FormattedMessage id="settings.plug" defaultMessage="Zásuvky" />}
        layout="list"
        visibility={{ variableId: 'PL_INSTALL_V1' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.enabled"
                defaultMessage="Zásuvky nainstalovány"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.plug.enabled.subtitle"
                defaultMessage="Zásuvky na lokální síti "
              />
            }
            switchVariableId="PL_INSTALL"
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.ip1"
                defaultMessage="IP adresa první zásuvky"
              />
            }
            inputVariableId="PL_IP[0]"
            visibility={{
              variableIds: [
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
                'PL_INSTALL',
              ],
            }}
            label={
              <FormattedMessage
                id="settings.plug.ip1.label"
                defaultMessage="IP 1"
              />
            }
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.enabled2"
                defaultMessage="Druhá zásuvka"
              />
            }
            visibility={{
              variableIds: [
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
                'PL_INSTALL',
              ],
            }}
            switchVariableId="PL_INSTALL_I[1]"
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.ip2"
                defaultMessage="IP adresa druhé zásuvky"
              />
            }
            inputVariableId="PL_IP[1]"
            visibility={{
              variableIds: [
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
                'PL_INSTALL_I[1]',
                'PL_INSTALL',
              ],
            }}
            label={
              <FormattedMessage
                id="settings.plug.ip2.label"
                defaultMessage="IP 2"
              />
            }
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.enabled3"
                defaultMessage="Třetí zásuvka"
              />
            }
            visibility={{
              variableIds: [
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
                'PL_INSTALL_I[1]',
                'PL_INSTALL',
              ],
            }}
            switchVariableId="PL_INSTALL_I[2]"
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.ip3"
                defaultMessage="IP adresa třetí zásuvky"
              />
            }
            inputVariableId="PL_IP[2]"
            visibility={{
              variableIds: [
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
                'PL_INSTALL_I[1]',
                'PL_INSTALL',
                'PL_INSTALL_I[2]',
              ],
            }}
            label={
              <FormattedMessage
                id="settings.plug.ip3.label"
                defaultMessage="IP 3"
              />
            }
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.modulenabled"
                defaultMessage="Zásuvky na modulu Shelly PRO 3"
              />
            }
            visibility={{
              variableIds: ['PL_INSTALL_V1', 'PL_INSTALL'],
            }}
            switchVariableId="PL_SHELLY_HW_PLUG"
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.modulip"
                defaultMessage="IP adresa modulu"
              />
            }
            inputVariableId="PL_IP[0]"
            visibility={{ variableId: 'PL_SHELLY_HW_PLUG' }}
            label={
              <FormattedMessage
                id="settings.plug.modulip.label"
                defaultMessage="IP Modulu"
              />
            }
            fullWidth
          />
        </ParentPaper>
      </Group>

      {relaySettings && (
        <>
          <Group
            title={
              <FormattedMessage
                id="settings.service.relay"
                defaultMessage="Relé"
              />
            }
            layout="list"
          >
            <ParentPaper>
              <SwitchBox
                title={
                  <FormattedMessage
                    id="settings.relay.enabled"
                    defaultMessage="Relé nainstalovány"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="settings.relay.enabled.subtitle"
                    defaultMessage="Integrovaná relé"
                  />
                }
                switchVariableId="RELAY_INSTALL"
                fullWidth
              />
              <SwitchBox
                title={
                  <FormattedMessage
                    id="settings.relay.user_advanced_control"
                    defaultMessage="Zobrazit stránku s informacemi uživateli"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="settings.relay.user_advanced_control.subtitle"
                    defaultMessage="Při povolení se objeví stránka v navigaci"
                  />
                }
                switchVariableId="RELAY_USER_ADVANCED_CONTROL"
                visibility={{ variableId: 'RELAY_INSTALL' }}
                fullWidth
              />
            </ParentPaper>
          </Group>

          <RelaySettingsService />
        </>
      )}

      <Group
        title={
          <FormattedMessage
            id="settings.service.network"
            defaultMessage="Síť"
          />
        }
        layout="list"
        help={{
          variant: 'dialog',
          title: (
            <FormattedMessage
              id="settings.service.network.help.title"
              defaultMessage="Nastavení sítě"
            />
          ),
          content: (
            <FormattedMessage
              id="settings.service.network.help.content"
              defaultMessage={`
                V seznamu je možné vidět aktuálně připojená zařízení do sítě Infigy (WiFi nebo kabelem), jejich IP adresy, MAC adresy, atd.
                Pokud zařízení používá DHCP, je možné kliknutím na špendlík a zafixovat IP adresu, kterou pak DHCP server vždy bude přiřazovat - toto je vhodné např. pro střídač, tepelné čerpadlo, apod.
                Pokud zařízení používá statickou IP adresu, nemusí se zobrazovat správně.
              `}
            />
          ),
        }}
      >
        <ParentPaper>
          <NetworkSettings />

          {networkCaps.managedNetwork && (
            <ControlBox
              title={
                <FormattedMessage
                  id="settings.service.network.wifi_passphrase"
                  defaultMessage="Heslo WiFi sítě"
                />
              }
              control={<WifiApPassphraseEdit />}
              fullWidth
            />
          )}
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.service.info"
            defaultMessage="Instalace"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <SettingsInstallation />
          <ControlBox
            title={
              <FormattedMessage
                id="settings.service.town"
                defaultMessage="Lokalita (obec)"
              />
            }
            visibility={{ variableId: 'BEZ_DODAVATELE_API_ENABLED' }}
            control={<TownCodeSelect />}
            fullWidth
          />
          <ButtonBox
            title={
              <FormattedMessage
                id="settings.service.reset"
                defaultMessage="Reset chyb"
              />
            }
            buttonLabel={
              <FormattedMessage
                id="settings.service.reset.button"
                defaultMessage="Reset chyb"
              />
            }
            onClick={resetErr}
            fullWidth
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.app_update"
            defaultMessage="Verze aplikace"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <AppUpdate />
          <VersionBox />
        </ParentPaper>
      </Group>

      <Group layout="list">
        <ParentPaper>
          <ButtonBox
            title={
              <FormattedMessage
                id="settings.service.exit"
                defaultMessage="Odhlásit ze servisu"
              />
            }
            buttonLabel={
              <FormattedMessage
                id="settings.service.exit.button"
                defaultMessage="Odhlásit"
              />
            }
            onClick={exitService}
            fullWidth
          />
        </ParentPaper>
      </Group>
    </>
  );

  return (
    <>
      <InitialWizardBanner
        variant="settings"
        sx={{ mt: 2, display: serviceActive ? 'block' : 'none' }}
      />

      {serviceActive ? content : servicePasswordInput}
    </>
  );
};
